import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Edit2, Eye, EyeOff } from "lucide-react";

// ─── Payment Instructions Panel ───────────────────────────────────────────────
export function MaskedSensitiveField({
  value,
  onChange,
  placeholder,
  testIdBase,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  testIdBase: string;
}) {
  const [revealed, setRevealed] = useState(false);
  const [editing, setEditing] = useState(false);
  const showInput = revealed || editing || !value;
  const last4 = value.slice(-4);
  const maskedDisplay = value.length <= 4 ? "•".repeat(value.length) : `••••${last4}`;

  return (
    <div className="mt-1 flex items-center gap-2">
      {showInput ? (
        <Input
          className="h-9 text-sm font-mono"
          type={revealed ? "text" : "password"}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => setEditing(false)}
          autoFocus={editing && !revealed}
          data-testid={`input-${testIdBase}`}
        />
      ) : (
        <div
          className="h-9 flex-1 flex items-center px-3 rounded-md border border-input bg-muted/40 text-sm font-mono tracking-wider"
          data-testid={`text-${testIdBase}-masked`}
        >
          {maskedDisplay}
        </div>
      )}
      <Button
        type="button"
        size="sm"
        variant="outline"
        className="h-9 px-2"
        onClick={() => {
          setRevealed((r) => !r);
          if (editing) setEditing(false);
        }}
        title={revealed ? "Hide" : "Show"}
        aria-label={revealed ? `Hide ${testIdBase}` : `Show ${testIdBase}`}
        data-testid={`button-toggle-${testIdBase}`}
      >
        {revealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
      </Button>
      {!showInput && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-9 px-2"
          onClick={() => setEditing(true)}
          title="Edit"
          aria-label={`Edit ${testIdBase}`}
          data-testid={`button-edit-${testIdBase}`}
        >
          <Edit2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
